import type { ControlResultListItemResponseDto } from "@/spz-api/model";
import type { TreeValue } from "@spz/ui/components/tree-searchable-select";
import type { Table } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Extracts conclusion IDs from the filter value with type safety
 * Handles both arrays and comma-separated strings from URL
 */
function extractIds(filterValue: unknown): string[] {
  if (Array.isArray(filterValue)) {
    return filterValue;
  }
  if (typeof filterValue === "string" && filterValue.length > 0) {
    return filterValue.split(",").filter((id) => id.length > 0);
  }
  return [];
}

/**
 * View model for conclusion/category filters
 */
export const useControlResultConclusionFiltersViewModel = ({
  table,
}: {
  table: Table<ControlResultListItemResponseDto>;
}) => {
  const [isConclusionFilterSelected, setIsConclusionFilterSelected] =
    useState(false);

  const conclusionValue = table.getColumn("conclusion_uuid")?.getFilterValue();
  const conclusionIds = useMemo(
    () => extractIds(conclusionValue),
    [conclusionValue],
  );

  const categoryValue = table
    .getColumn("conclusion_category_uuid")
    ?.getFilterValue();
  const categoryIds = useMemo(() => extractIds(categoryValue), [categoryValue]);

  // Derive value directly from table filters instead of using separate state
  const value = useMemo<TreeValue>(
    () => ({
      selectedSectionIds: categoryIds,
      selectedOptionValues: conclusionIds,
    }),
    [categoryIds, conclusionIds],
  );

  const handleModeToggle = useCallback(() => {
    setIsConclusionFilterSelected((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (newValue: TreeValue) => {
      // Update table filters for both conclusions and categories
      table.setColumnFilters((current) => {
        const filtered = current.filter(
          (f) =>
            f.id !== "conclusion_uuid" && f.id !== "conclusion_category_uuid",
        );

        const newFilters = [...filtered];

        // Add conclusion filter if there are selected conclusions
        if (newValue.selectedOptionValues.length > 0) {
          newFilters.push({
            id: "conclusion_uuid",
            value: newValue.selectedOptionValues,
          });
        }

        // Add category filter if there are selected categories
        if (newValue.selectedSectionIds.length > 0) {
          newFilters.push({
            id: "conclusion_category_uuid",
            value: newValue.selectedSectionIds,
          });
        }

        return newFilters;
      });
    },
    [table],
  );

  // Handle column visibility and filter reset when mode changes
  useEffect(() => {
    // Toggle column visibility
    const conclusionColumn = table.getColumn("conclusion_uuid");
    const categoryColumn = table.getColumn("conclusion_category_uuid");

    conclusionColumn?.toggleVisibility(isConclusionFilterSelected);
    categoryColumn?.toggleVisibility(!isConclusionFilterSelected);
  }, [table, isConclusionFilterSelected]);

  return {
    isConclusionFilterSelected,
    value,
    onSelect: handleSelect,
    onModeToggle: handleModeToggle,
  };
};
